import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { ClipboardCheck } from 'lucide-react';

import { useUser } from '../../contexts/UserContext';
import PrivateLayout from '../layouts/PrivateLayout';
import Loading from '../common/Loading';
import { useFetchClaimProfile } from '../../services/claims';
import {
  useUpdateClaimActivity,
  useDeleteClaimActivity,
} from '../../services/activity';
import SideBar from './components/SideBar';
import Tabs from './components/Tabs';
import ActivityTab from './components/ActivityTab';
import ClaimDetailsTab from './components/ClaimDetailsTab';
import FileBrowser from '../fileBrowser/FileBrowser';
import TranscriptsTab from './components/TranscriptsTab';
import GeneratedContentTab from './components/GeneratedContentTab';
import ActivityModal from './components/ActivityModal';
import CaseGPTChat from '../casegpt/Chat';
import DbqTab from './components/DbqTab';
import BillingTab from './components/BillingTab';
import {
  useFetchBillingInvoices,
  useFetchPaymentPlanSummary,
} from '../../services/billing';
import { useOnboardingByClaim } from '../../services/clientOnboarding';

const FloatingButton = styled.button`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  transition: all 0.2s ease;
  z-index: 1040;
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 28px rgba(99, 102, 241, 0.55);
  }
`;

const ProgressBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #fff;
  color: #4338ca;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 99px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  line-height: 1.2;
`;

const Dashboard = () => {
  const { user, hasPermission, hasAnyPermission } = useUser();
  const { claimId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canViewBilling = hasAnyPermission([
    // 'claims:view_own',
    // 'claims:view_assigned',
    // 'claims:view_all',
    'billing:view',
    'billing:manage',
  ]);
  const canManageBilling = hasPermission('billing:manage');
  // The Case Readiness slide-out is gone (2026-09-28): the checklist lives
  // on Client Onboarding now; this button shows where they stand and opens
  // them there.
  const canViewOnboarding =
    hasPermission('client_onboarding:work') || hasPermission('boards:view');
  const { isLoading, data, isError, error } = useFetchClaimProfile(claimId);

  // Handle the "stale dashboard click" case: the dashboard cache had a claim
  // the caller can no longer fetch (provider lost access because the claim
  // moved into a restricted status, claim got deleted, permissions revoked,
  // etc.). Without this guard, SideBar / Tabs crash trying to read
  // `data.tasks` / `data.claim` on undefined data. Toast wording is
  // provider-flavored only for providers; everyone else gets a neutral
  // "claim not found". Also invalidates the dashboard list so the stale
  // card disappears immediately, not on next manual refresh.
  useEffect(() => {
    if (!isError) return;
    const status = error?.response?.status;
    if (status !== 404 && status !== 403) return;

    const isProvider = user?.userType === 'provider';
    if (isProvider) {
      toast.info(
        'This claim has moved on. Nothing further needed on your side.',
      );
    } else {
      toast.error('Claim not found.');
    }

    // Drop the stale card from the dashboard before we land back on it.
    // useFetchDashboardClaims and useFetchClaim both use the `['claim', ...]`
    // key prefix — invalidating the prefix refreshes both.
    queryClient.invalidateQueries({ queryKey: ['claim'] });

    navigate('/dashboard');
  }, [isError, error, navigate, user, queryClient]);
  const { data: invoices } = useFetchBillingInvoices(claimId, {
    enabled: canViewBilling,
  });
  const { data: planSummary } = useFetchPaymentPlanSummary(claimId, {
    enabled: canViewBilling,
  });
  // Plan-covered invoices (or deposits still clearing) are agreed to be
  // paid — the outstanding-invoice banner only fires for invoices no live
  // plan is paying down.
  const planCoveredInvoiceIds = new Set(
    (planSummary?.plans || [])
      .filter(
        (plan) =>
          ['active', 'past_due'].includes(plan.status) ||
          plan?.metadata?.depositStatus === 'processing',
      )
      .flatMap((plan) => plan.sourceInvoiceIds || []),
  );
  const hasActionableOpenInvoice = invoices?.some(
    (inv) =>
      inv.invoiceStatus === 'open' && !planCoveredInvoiceIds.has(inv.invoiceId),
  );
  const { data: onboardingSummary } = useOnboardingByClaim(
    claimId,
    canViewOnboarding,
  );
  const [showActivity, setShowActivity] = useState(null);
  const [initialTabIndex, setInitialTabIndex] = useState(0);


  const updateClaimActivity = useUpdateClaimActivity(() => {
    toast.success('The activity has been successfully completed.');
    queryClient.invalidateQueries({ queryKey: ['claim-profile'] });
  });

  const deleteClaimActivity = useDeleteClaimActivity(() => {
    toast.success('The activity has been successfully deleted.');
    queryClient.invalidateQueries({ queryKey: ['claim-profile'] });
  });

  const tabs = useMemo(() => {
    if (!data) return [];

    const nextTabs = [
      {
        label: 'Activity',
        content: (
          <ActivityTab
            claim={data.claim}
            activity={data.activity}
            onActivityAction={(action, activity) => {
              switch (action) {
                case 'edit':
                  setShowActivity(activity);
                  break;
                case 'delete':
                  if (
                    window.confirm(
                      'Are you sure you want to delete this activity? This cannot be undone.',
                    )
                  ) {
                    deleteClaimActivity.mutate({
                      claimId,
                      actId: activity.actId,
                    });
                  }
                  break;
                case 'complete':
                  if (
                    window.confirm(
                      'Are you sure you want to mark this item as completed?',
                    )
                  ) {
                    updateClaimActivity.mutate({
                      claimId,
                      actId: activity.actId,
                      payload: {
                        actCompletedBy: user.userId,
                        actCompletedOn: moment().toDate(),
                      },
                    });
                  }
                  break;
                default:
                // do nothing
              }
            }}
          />
        ),
      },
      {
        label: 'Claim Details',
        content: <ClaimDetailsTab claim={data.claim} />,
      },
      {
        label: 'Files',
        content: (
          <Card>
            <Card.Body>
              <FileBrowser
                claimId={claimId}
                files={data.claim.files}
                onRefresh={() => {
                  queryClient.invalidateQueries(['claim-profile', claimId]);
                }}
              />
            </Card.Body>
          </Card>
        ),
      },
      {
        label: 'Transcripts',
        content: (
          <TranscriptsTab
            claim={data.claim}
            transcripts={data.transcripts}
            questionnaireMatrix={data.questionnaireMatrix}
          />
        ),
      },
      {
        label: 'Generated Content',
        content: (
          <GeneratedContentTab
            claim={data.claim}
            addons={data.addons}
            questionnaireMatrix={data.questionnaireMatrix}
          />
        ),
      },
      {
        label: 'DBQ',
        content: (
          <DbqTab
            claimId={claimId}
            claimStatus={data.claim?.claimStatus || ''}
            providers={data.providers || []}
            claimFiles={data.claim?.files || []}
          />
        ),
      },
    ];

    if (canViewBilling) {
      nextTabs.push({
        key: 'billing',
        label: 'Billing',
        content: (
          <BillingTab
            claimId={claimId}
            invoices={invoices}
            isAdmin={canManageBilling}
            billingFrozen={['dead', 'dispute hold'].includes(
              data.claim?.claimStatus,
            )}
            disputeHold={
              data.claim?.claimStatus === 'dispute hold'
                ? {
                    at: data.claim?.disputeHoldAt,
                    source: data.claim?.disputeHoldSource,
                    note: data.claim?.disputeHoldNote,
                  }
                : null
            }
          />
        ),
      });
    }

    if (hasAnyPermission(['claims:view_all', 'claims:view_assigned'])) {
      nextTabs.push({
        label: 'Chat (beta)',
        content: (
          <div>
            <CaseGPTChat data={data} />
          </div>
        ),
      });
    }

    return nextTabs;
  }, [
    claimId,
    data,
    deleteClaimActivity,
    invoices,
    queryClient,
    updateClaimActivity,
    user.userId,
    hasAnyPermission,
    canManageBilling,
    canViewBilling,
  ]);

  const tabLabels = useMemo(
    () => tabs.map((tab) => tab.label.toLowerCase()),
    [tabs],
  );

  // Handle tab query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabLabels.length > 0) {
      const tabIndex = tabLabels.findIndex(
        (label) => label === tabParam.toLowerCase(),
      );
      if (tabIndex >= 0) {
        setInitialTabIndex(tabIndex);
      }
    }
  }, [searchParams, tabLabels]);

  return (
    <PrivateLayout>
      {/* Hidden while the Billing tab is open — the banner's job is routing
          there, not narrating what's already on screen. */}
      {canViewBilling &&
        data &&
        hasActionableOpenInvoice &&
        searchParams.get('tab') !== 'billing' && (
          <Alert variant="danger" className="mb-3">
            This claim has an outstanding invoice. Please review the{' '}
            <Alert.Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSearchParams({ tab: 'billing' });
              }}
            >
              Billing tab
            </Alert.Link>
            .
          </Alert>
        )}
      {data &&
        user.userType !== 'client' &&
        invoices?.some((inv) => inv.invoiceStatus === 'draft') && (
          <Alert variant="warning" className="mb-3">
            This claim has an invoice pending review. Please review the{' '}
            <Alert.Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSearchParams({ tab: 'billing' });
              }}
            >
              Billing tab
            </Alert.Link>
            .
          </Alert>
        )}
      {isError ? // Render nothing instead of <Loading /> so the bounce-back feels // 404/403 path — useEffect above is invalidating + navigating away.
      // instant (no spinner flash).
      null : isLoading || !data ? (
        <Loading />
      ) : (
        <>
          <Row>
            <Col xs={12} sm={5} md={4}>
              <SideBar
                data={data}
                user={user}
                onCreateActivityClicked={() =>
                  setShowActivity({ actId: 'new' })
                }
              />
            </Col>
            <Col>
              <Tabs
                tabs={tabs}
                defaultSelectedIndex={initialTabIndex}
                onTabChange={(tab) =>
                  setSearchParams({ tab: tab.label.toLowerCase() })
                }
              />
            </Col>
          </Row>
        </>
      )}
      <ActivityModal
        claimId={claimId || ''}
        activity={showActivity || {}}
        show={showActivity !== null}
        toggle={() => setShowActivity(null)}
      />
      {canViewOnboarding && onboardingSummary?.onboardingId ? (
        <FloatingButton
          title="Open their onboarding checklist"
          onClick={() =>
            navigate(
              `/operations/client-onboarding?open=${onboardingSummary.onboardingId}`,
            )
          }
        >
          <ClipboardCheck size={22} />
          <ProgressBadge>
            {onboardingSummary.progress.done}/{onboardingSummary.progress.total}
          </ProgressBadge>
        </FloatingButton>
      ) : null}
    </PrivateLayout>
  );
};

export default Dashboard;
